import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { residentStatusLabel } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { owes } from "@/components/app/payments/money";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useFacility } from "@/lib/facility/facility";
import { guardRoute } from "@/lib/policy/route-guard";
import { orpc } from "@/lib/orpc";
import { optionalDay, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import {
  WorklistRow,
  DASH,
} from "@/routes/_authenticated/_financials/collections/-components/collections-shared";
import { Contact } from "@/routes/_authenticated/_financials/collections/-components/collections-contact";
import { linkClass } from "@/components/app/lists/record-link-shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/_financials/collections/")({
  validateSearch: (search: Record<string, unknown>): { asOf?: string; scope?: "all" } => ({
    asOf: optionalDay(search.asOf),
    scope: optionalOneOf(["all"] as const, search.scope),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "collections"),
  component: Collections,
});

const BUCKETS = [
  { key: "current", header: "Current" },
  { key: "1-30", header: "1-30" },
  { key: "31-60", header: "31-60" },
  { key: "61-90", header: "61-90" },
  { key: "90+", header: "90+" },
] as const;

/* Hoisted: the columns close over nothing, and a table is not a component. */
const COLUMNS: ReadonlyArray<ReportColumn<WorklistRow>> = [
  {
    header: "Resident",
    cell: (row) => (
      <PersonCell
        kind="resident"
        name={row.residentName}
        renderLink={(name) => (
          <Link
            to="/residents/$residentId"
            params={{ residentId: row.residentId }}
            className={cn(linkClass, "truncate")}
          >
            {name}
          </Link>
        )}
        meta={[
          row.roomNumber ? `Room ${row.roomNumber}` : "No room",
          `${row.openInvoiceCount} open`,
          row.residentStatus !== "active" ? residentStatusLabel(row.residentStatus) : null,
        ]}
      />
    ),
    value: (row) => row.residentName,
  },
  // Folded into the resident cell on screen; their own columns in the file, as before.
  { header: "Room", cell: (row) => row.roomNumber ?? "", csvOnly: true },
  { header: "Status", cell: (row) => row.residentStatus, csvOnly: true },
  ...BUCKETS.map(({ key, header }): ReportColumn<WorklistRow> => ({
    header,
    /* A bucket somebody owes nothing in is a dash, not "$0.00": five zeroes
         across a row is noise to read past when the point is which column the
         debt sits in. */
    cell: (row) =>
      row.buckets[key] === "$0.00" ? (
        DASH
      ) : (
        // Anything past current is overdue money, set in the warn ink.
        <span className={key === "current" ? undefined : "text-warn font-medium"}>
          {row.buckets[key]}
        </span>
      ),
    value: (row) => row.buckets[key],
    align: "right",
  })),
  { header: "Total", cell: (row) => row.total, value: (row) => row.total, align: "right" },
  {
    header: "Oldest",
    cell: (row) => (
      <span className={row.oldestDays >= 90 ? "text-crit font-medium" : undefined}>
        {row.oldestDays}d
      </span>
    ),
    value: (row) => row.oldestDays,
    align: "right",
  },
  {
    header: "Last paid",
    cell: (row) => row.lastPaymentDate ?? DASH,
    value: (row) => row.lastPaymentDate,
  },
  {
    header: "Contact",
    cell: (row) => <Contact contact={row.contact} />,
    value: (row) => row.contact?.name ?? "",
  },
  { header: "Phone", cell: (row) => row.contact?.phone ?? "", csvOnly: true },
  { header: "Email", cell: (row) => row.contact?.email ?? "", csvOnly: true },
];

/** Collections worklist: one row per resident, not per invoice, because families are rung about
 * everything outstanding. Buckets, oldest debt age, last payment and contact share the line. The
 * flat `items` list would duplicate AR Summary. */
function Collections() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: { asOf?: string; scope?: "all" }) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const asOf = params.asOf ?? today;
  const setAsOf = (next: string) => patch({ asOf: next === today || !next ? undefined : next });
  const overdueOnly = params.scope !== "all";
  const setOverdueOnly = (next: boolean) => patch({ scope: next ? undefined : "all" });

  const aging = useQuery(
    orpc.financials.reports.arAging.queryOptions({ input: { facilityId, asOf, overdueOnly } }),
  );
  const totals = aging.data?.rowTotals;

  return (
    <>
      <PageHeader
        title="Collections"
        count={aging.data?.rows.length}
        meta="Open balances by age, oldest first, with the person to contact about each one."
      />

      <div className="flex flex-wrap items-end gap-6">
        <div className="space-y-1">
          <Label htmlFor="aged-as-of">Aged as of</Label>
          <Input
            id="aged-as-of"
            type="date"
            value={asOf}
            onChange={(event) => setAsOf(event.target.value)}
            className="w-40"
          />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch id="overdue-only" checked={overdueOnly} onCheckedChange={setOverdueOnly} />
          <Label htmlFor="overdue-only">Overdue only</Label>
        </div>
      </div>

      {totals ? (
        <StatTiles>
          {BUCKETS.map(({ key, header }) => (
            <StatTile
              key={key}
              label={header === "Current" ? "Current" : `${header} days`}
              value={totals[key]}
              // Money three months late is the worklist's point; a month or two is a nudge.
              tone={
                !owes(totals[key])
                  ? undefined
                  : key === "90+"
                    ? "crit"
                    : key === "61-90"
                      ? "warn"
                      : undefined
              }
            />
          ))}
          <StatTile label="Total owed" value={totals.total} />
        </StatTiles>
      ) : null}

      {/* A failed read is not an empty report. */}
      {aging.isError ? (
        <ReadFailed
          message="The collections list could not be loaded."
          error={aging.error}
          onRetry={() => void aging.refetch()}
          retrying={aging.isFetching}
        />
      ) : (
        <ReportTable
          rows={aging.data?.rows}
          pending={aging.isPending}
          filename="collections.csv"
          noun="residents"
          rowKey={(row) => row.residentId}
          columns={COLUMNS}
          empty={{
            title: "Nothing outstanding",
            description: "Every bill that has come due has been settled.",
          }}
        />
      )}
    </>
  );
}
